import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { Route, Routes } from "react-router";

import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import Box from "ds/components/Box";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import Tab from "ds/components/Tab";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { BillingAddonType } from "types/generated";
import { isStripeEnabled } from "utils/stripe";
import { AccountContext } from "views/AccountWrapper";

import BillingPaymentFailureCallout from "./components/PaymentFailureCallout";
import { BillingContext } from "./Context";
import BillingDashboard from "./Dashboard";
import { GET_BILLING } from "./gql";
import BillingLegacySubscriptionManagement from "./LegacySubscriptionManagement";
import BillingSubscriptionManagement from "./SubscriptionManagement";
import { GetBillingGql } from "./types";
import BillingUpgrade from "./Upgrade";
import { isLegacyBillingTier, isSelfServiceTier } from "./utils";

const Billing = () => {
  const { accountName } = useTypedContext(AccountContext);
  useTitle(`Organization Settings · Billing · ${accountName}`);

  const { error, loading, data } = useQuery<GetBillingGql>(GET_BILLING, {
    fetchPolicy: "no-cache",
  });

  const ErrorContent = useErrorHandle(error);

  const currentAddons: BillingAddonType[] = useMemo(
    () => data?.billingSubscription?.addons?.map((addon) => addon.name) || [],

    [data?.billingSubscription?.addons]
  );

  if (ErrorContent) {
    return ErrorContent;
  }

  // TODO: maybe replace with skeleton loader
  if (!data && loading) {
    return <PageLoading />;
  }

  // TODO: replace with some empty or error state
  if (!data) {
    return <NotFoundPage />;
  }

  const isLegacy = isLegacyBillingTier(data.tier);
  const isSelfService = isSelfServiceTier(data.tier);

  const hasActiveSubscription =
    !!data.billingSubscription?.isActive &&
    !data.billedExternally &&
    !data.onTrialUntil &&
    (isSelfService || isLegacy);

  const billingSubscription = hasActiveSubscription ? data.billingSubscription : undefined;

  const SubscriptionManagementComponent = isSelfService
    ? BillingSubscriptionManagement
    : BillingLegacySubscriptionManagement;

  return (
    <BillingContext.Provider
      value={{
        tier: data.tier,
        billedExternally: data.billedExternally,
        billingSubscription,
        onTrialUntil: data.onTrialUntil,
        isTrial: !!data.onTrialUntil,
        currentAddons,
        availableBillingAddons: data.availableBillingAddons,
        availableSelfServicePlans: data.availableSelfServicePlans,
      }}
    >
      {!!billingSubscription && <BillingPaymentFailureCallout subscription={billingSubscription} />}

      <PageInfoHeaderWrapper>
        <Box direction="column">
          <PageInfoHeaderTitle title="Billing" />

          {!!billingSubscription && (
            <ViewHeaderNavigation>
              <Tab end to=".." label="Dashboard" />
              <Tab to="../subscription" label="Subscription" />
            </ViewHeaderNavigation>
          )}
        </Box>
      </PageInfoHeaderWrapper>
      <Routes>
        <Route index element={<BillingDashboard />} />
        {!!billingSubscription && (
          <Route
            path="subscription"
            element={<SubscriptionManagementComponent billingSubscription={billingSubscription} />}
          />
        )}
        {isStripeEnabled() && <Route path="upgrade" element={<BillingUpgrade />} />}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BillingContext.Provider>
  );
};

export default Billing;
